import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('loading');
  const [details, setDetails] = useState(null);
  const [tries, setTries] = useState(0);
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      setTries(attempts);
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          setDetails(res.data);
          clearCart();
          return;
        }
        if (attempts >= 12) {
          setStatus('timeout');
          setDetails(res.data);
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full bg-[#efe9db] p-10 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-14 h-14 mx-auto mb-6 text-[#3a4a3b] animate-spin" />
            <h1 className="font-serif-elegant text-3xl mb-3">Confirming your order</h1>
            <p className="text-[#4a4640] text-sm">Just a moment while we check with Stripe... (attempt {tries})</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto mb-6 text-[#3a4a3b]" />
            <h1 className="font-serif-elegant text-4xl mb-3">Thank you!</h1>
            <p className="text-[#4a4640] mb-6">
              Your order is confirmed. We&rsquo;re preparing your herbs with love and will email you when it ships.
            </p>
            {details?.amount && (
              <div className="text-sm text-[#6f6a5f] mb-6">
                Total paid: <span className="font-medium text-[#2f2a24]">${(details.amount / 100).toFixed(2)}</span>
              </div>
            )}
            <Link to="/collections/all" className="btn-primary">Continue Shopping</Link>
          </>
        )}
        {status === 'timeout' && (
          <>
            <Loader2 className="w-14 h-14 mx-auto mb-6 text-[#3a4a3b]" />
            <h1 className="font-serif-elegant text-3xl mb-3">Still confirming...</h1>
            <p className="text-[#4a4640] mb-6">Your payment is still being confirmed. You can safely refresh this page in a minute.</p>
            <button onClick={() => window.location.reload()} className="btn-outline">Refresh</button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-14 h-14 mx-auto mb-6 text-[#7a3b2e]" />
            <h1 className="font-serif-elegant text-3xl mb-3">Something went wrong</h1>
            <p className="text-[#4a4640] mb-6">We couldn&rsquo;t confirm the payment. If you were charged, please contact us and we&rsquo;ll sort it right away.</p>
            <Link to="/cart" className="btn-primary">Back to Cart</Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full bg-[#efe9db] p-10 text-center">
        <XCircle className="w-14 h-14 mx-auto mb-6 text-[#7a3b2e]" />
        <h1 className="font-serif-elegant text-4xl mb-3">Checkout canceled</h1>
        <p className="text-[#4a4640] mb-6">No worries &mdash; your cart is still saved. Take your time.</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/cart" className="btn-outline">Back to Cart</Link>
          <Link to="/collections/all" className="btn-primary">Keep Shopping</Link>
        </div>
      </div>
    </div>
  );
};
